import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { CycleDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { CoverageBar } from '@/components/common/CoverageBar';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { PublicationByProductLink } from '@/components/user_catalog/publications/PublicationByProductLink';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationByProduct } from '@/components/user_catalog/publications/usePublicationByProduct';
import { CycleKindTag, CycleStatusTag } from '@/components/settlement/tags';
import {
  formatDate,
  formatDateTime,
  formatFraction,
  formatSecondsClock,
} from '@/utils/format';

type Props = {
  record: CycleDetailOut;
};

/** Seconds rendered as a clock, with the raw count one hover away. */
const SecondsCell = ({ value }: { value?: number | null }) =>
  orDash(
    value != null && (
      <Tooltip title={`${value} s`}>
        <span>{formatSecondsClock(value, { alwaysHours: true })}</span>
      </Tooltip>
    )
  );

export const CycleGeneralTab = ({ record }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  // Shares its request with the product link in the same table.
  const kind = productKind(
    usePublicationByProduct(record.product_id),
    record.product_id
  );

  const duration = record.product_duration;
  const ranges = record.fragment_ranges ?? [];

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'publisher_id',
      label: t('settlement.cycles.fields.publisher_id'),
      children: orDash(
        record.publisher_id != null && (
          <PublisherIconLink publisherId={record.publisher_id} />
        )
      ),
    },
    {
      key: 'account_id',
      label: t('settlement.cycles.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t('settlement.cycles.fields.profile_id'),
      children: orDash(
        record.profile_id != null && (
          <ProfileIconLink profileId={record.profile_id} />
        )
      ),
    },
    {
      key: 'product_id',
      label: t('settlement.cycles.fields.product_id'),
      children: orDash(
        record.product_id != null && (
          <PublicationByProductLink productId={record.product_id} />
        )
      ),
    },
    {
      key: 'product_version',
      label: t('settlement.cycles.fields.product_version'),
      children: orDash(record.product_version),
    },
    {
      key: 'product_format',
      label: t('settlement.cycles.fields.product_format'),
      children: orDash(kind && <ProductFormatTag kind={kind} />),
    },
    {
      key: 'started_at',
      label: t('settlement.cycles.fields.started_at'),
      children: orDash(formatDate(record.started_at)),
    },
    {
      // The window opens on the min-threshold crossing, not on the first
      // consumption; a generation that never crossed has no window yet.
      key: 'window',
      label: t('settlement.cycles.fields.window'),
      children: orDash(
        record.min_threshold_crossed_at &&
          `${formatDate(record.min_threshold_crossed_at)} → ${
            record.expires_at ? formatDate(record.expires_at) : '—'
          }`
      ),
    },
    {
      key: 'status',
      label: t('settlement.cycles.fields.status'),
      children: (
        <CycleStatusTag
          status={record.status}
          closeReason={record.close_reason}
        />
      ),
    },
    {
      key: 'closed_at',
      label: t('settlement.cycles.fields.closed_at'),
      children: orDash(formatDateTime(record.closed_at)),
    },
  ];

  const contractItems: DescriptionsProps['items'] = [
    {
      key: 'contract_rate_id',
      label: t('settlement.cycles.fields.contract_rate_id'),
      children: orDash(record.contract_rate_id),
    },
    {
      key: 'cycle_kind',
      label: t('settlement.cycles.fields.cycle_kind'),
      children: <CycleKindTag value={record.cycle_kind} />,
    },
    {
      key: 'cycle_length_days',
      label: t('settlement.cycles.fields.cycle_length_days'),
      children: orDash(record.cycle_length_days),
    },
    {
      key: 'max_consumption_threshold',
      label: t('settlement.cycles.fields.max_consumption_threshold'),
      children: orDash(
        record.max_consumption_threshold != null &&
          `${Math.round(Number(record.max_consumption_threshold) * 100)}%`
      ),
    },
    {
      key: 'max_billable_cycles',
      label: t('settlement.cycles.fields.max_billable_cycles'),
      children: orDash(record.max_billable_cycles),
    },
    {
      key: 'pass_number',
      label: t('settlement.cycles.fields.pass_number'),
      children: (
        <Tooltip title={t('settlement.cycles.hints.pass_number')}>
          <span>{record.pass_number}</span>
        </Tooltip>
      ),
    },
    {
      key: 'min_threshold_crossed_at',
      label: t('settlement.cycles.fields.min_threshold_crossed_at'),
      children: orDash(formatDateTime(record.min_threshold_crossed_at)),
    },
    {
      key: 'max_threshold_crossed_at',
      label: t('settlement.cycles.fields.max_threshold_crossed_at'),
      children: orDash(formatDateTime(record.max_threshold_crossed_at)),
    },
  ];

  const timeItems: DescriptionsProps['items'] = [
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          <Card title={t('settlement.cycles.sections.coverage')}>
            <Flex vertical gap={token.margin}>
              <Descriptions
                {...verticalDescProps}
                column={{ xs: 1, sm: 2 }}
                items={[
                  {
                    key: 'covered_seconds',
                    label: t('settlement.cycles.fields.covered_seconds'),
                    children: <SecondsCell value={record.covered_seconds} />,
                  },
                  {
                    key: 'covered_fraction',
                    label: t('settlement.cycles.fields.covered_fraction'),
                    children: orDash(
                      record.covered_fraction != null && (
                        <Tooltip title={record.covered_fraction}>
                          <span>{formatFraction(record.covered_fraction)}</span>
                        </Tooltip>
                      )
                    ),
                  },
                  {
                    key: 'product_duration',
                    label: t('settlement.cycles.fields.product_duration'),
                    children: <SecondsCell value={duration} />,
                  },
                ]}
              />

              <div>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM }}
                >
                  {t('settlement.cycles.fields.fragment_ranges')}
                </Typography.Text>
                {/* Ranges are product seconds; CoverageBar wants fractions of
                    the whole, so scale them by the product duration. */}
                {ranges.length && duration > 0 ? (
                  <CoverageBar
                    ranges={ranges.map((range) => ({
                      lower: range.lower / duration,
                      upper: range.upper / duration,
                    }))}
                    totalSeconds={duration}
                    hint={t('settlement.cycles.hints.fragment_ranges')}
                  />
                ) : (
                  <div>{orDash(null)}</div>
                )}
              </div>
            </Flex>
          </Card>

          <Card title={t('base.sections.system_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={timeItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Card title={t('settlement.cycles.sections.contract')}>
          <Descriptions
            {...verticalDescProps}
            column={{ xs: 1, sm: 2 }}
            items={contractItems}
          />
        </Card>
      </Col>
    </Row>
  );
};
